/**
* PURPOSE: Página de selección de organizaciones habilitadas para el usuario.
* RESPONSIBILITIES: Redirigir usuarios one-company y cargar companies según rol/is_one_company.
* COLLABORATORS: src/features/authSlice.js, src/services/company.js, src/utils/authUser.js, src/sections/CompaniesSection.jsx
* ROUTE: src/pages/Companies.jsx
*/
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Layout from "./Layout";
import CompaniesSection from "../sections/CompaniesSection";
import { getMe } from "../features/authSlice.js";
import { getCompanies, getMenuCompanies } from "../services/company.js";
import {
  buildCampeonatosUrlByCompany,
  getUserCompanyId,
  getUserRole,
  isOneCompanyUser,
} from "../utils/authUser.js";

const Companies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isError } = useSelector((state) => state.auth);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    dispatch(getMe());
  }, [dispatch]);

  useEffect(() => {
    if (isError && !user) navigate("/");
  }, [isError, user, navigate]);

  useEffect(() => {
    if (!user) return;

    const role = getUserRole(user);
    const companyId = getUserCompanyId(user);
    const oneCompany = isOneCompanyUser(user);

    if (!oneCompany) return;

    if (role === 1 || role === 4) {
      if (companyId) {
        navigate(buildCampeonatosUrlByCompany(companyId), { replace: true });
      } else {
        navigate("/campeonatos", { replace: true });
      }
      return;
    }

    if (role === 2 || role === 3) {
      navigate("/campeonatos", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    let cancelled = false;

    const loadCompanies = async () => {
      if (!user?.iduser) return;

      try {
        setLoading(true);
        setError(null);

        const role = getUserRole(user);
        const oneCompany = isOneCompanyUser(user);

        let data = [];

        if (role === 1 && !oneCompany) {
          data = await getCompanies();
        } else if ([2, 3, 4].includes(role) && !oneCompany) {
          data = await getMenuCompanies({ iduser: user.iduser });
        }

        if (!cancelled) setCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching companies:", err);
        if (!cancelled) {
          setCompanies([]);
          setError("No se pudo cargar la lista de organizaciones.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCompanies();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <Layout>
      <CompaniesSection companies={companies} loading={loading} error={error} />
    </Layout>
  );
};

export default Companies;
